import { hasValue } from "@repo/helpers";
import { MouseEvent as ReactMouseEvent, useCallback, useEffect, useRef, useState } from "react";

import { ModalMarkup } from "./markup";
import { Props } from "./types";

export function Modal(props: Props) {
	const initialY = useRef(0);
	const oldClientX = useRef(0);
	const oldClientY = useRef(0);
	const clientX = useRef(0);
	const clientY = useRef(0);
	const modalRef = useRef<HTMLDivElement>();
	const modalContentRef = useRef<HTMLDivElement>();

	const [visible, setVisible] = useState(props.visible);

	useEffect(() => {
		document.body.onkeydown = handleEscapePress;
	}, []);

	useEffect(() => {
		setVisible(props.visible);
	}, [props.visible]);

	useEffect(() => {
		if (props.visible && hasValue(modalContentRef.current)) {
			modalContentRef.current.style.removeProperty("margin");
			modalContentRef.current.style.removeProperty("top");
			modalContentRef.current.style.removeProperty("left");
			initialY.current = modalContentRef.current.offsetTop;
		}
	}, [visible]);

	/**
	 * If a close function is defined, call it, else
	 * set visible to false
	 */
	const closeModal = useCallback(() => {
		if (props.closeFunction) {
			props.closeFunction();
		} else {
			setVisible(false);
		}
	}, [props.closeFunction]);

	/**
	 * If the user presses the escape key and keyboardClose is true then close the modal
	 * @param event
	 */
	function handleEscapePress(event: KeyboardEvent) {
		if (event.key !== "Escape" || !props.keyboardClose || !props.visible) return;
		closeModal();
	}

	/**
	 * Close modal on click off if clickOff is true
	 * @param event: Mouse event
	 * @param content: Whether the click was on the modal content or on the overlay
	 */
	const handleClickOffEvent = useCallback(
		(event: ReactMouseEvent<HTMLDivElement>, content: boolean) => {
			if (event.currentTarget === null) return;
			if (content) {
				event.stopPropagation();
			} else if (props.clickOff) {
				closeModal();
			}
		},
		[closeModal, props.clickOff],
	);

	const moveMouseMove = useCallback((event: MouseEvent) => {
		event.preventDefault();
		if (!hasValue(modalContentRef.current)) return;
		let mouseX = event.clientX;
		if (mouseX > modalRef.current.clientWidth) mouseX = modalRef.current.clientWidth;
		if (mouseX <= 0) mouseX = 0;
		let mouseY = event.clientY;
		if (mouseY > modalRef.current.clientHeight) mouseY = modalRef.current.clientHeight;
		if (mouseY <= 0) mouseY = 0;
		clientX.current = oldClientX.current - mouseX;
		clientY.current = oldClientY.current - mouseY;
		oldClientX.current = mouseX;
		oldClientY.current = mouseY;

		modalContentRef.current.style.top =
			modalContentRef.current.offsetTop - clientY.current - initialY.current + "px";
		modalContentRef.current.style.left = modalContentRef.current.offsetLeft - clientX.current + "px";
	}, []);

	const moveMouseDown = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
		if (!hasValue(modalContentRef.current)) return;
		oldClientX.current = event.clientX;
		oldClientY.current = event.clientY;
		const boundingBox = modalContentRef.current.getBoundingClientRect();
		modalContentRef.current.style.left = boundingBox.left + "px";
		modalContentRef.current.style.margin = "0";
		document.addEventListener("mousemove", moveMouseMove);
		document.addEventListener("mouseup", moveMouseUp);
	}, []);

	const moveMouseUp = useCallback(() => {
		clientX.current = 0;
		clientY.current = 0;
		oldClientX.current = 0;
		oldClientY.current = 0;
		document.removeEventListener("mousemove", moveMouseMove);
		document.removeEventListener("mouseUp", moveMouseUp);
	}, []);

	return (
		<ModalMarkup
			{...props}
			visible={visible}
			moveMouseDown={moveMouseDown}
			modalRef={modalRef}
			modalContentRef={modalContentRef}
			closeModal={closeModal}
			handleOffClick={handleClickOffEvent}
		/>
	);
}

export * as ModalOptions from "./types";
